import React, {
    createContext,
    useContext,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';

import Translations from '$data/translations';

import { getLocation } from '$utilities/location';

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

const noop = async () => { };

const LocaleContext = createContext({
    current: null,
    options: [],
    formatURI: noop,
    translate: noop,
});

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * @hook useSession
 */
const useLocale = () => {
    const context = useContext(LocaleContext);

    if (context === undefined) {
        throw new Error('useLocale must be used within a LocaleProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Provider(s) */
/* ========================================================================= */

/**
 * @component LocaleProvider
 * @props (see propTypes)
 */
const LocaleProvider = ({
    children,
    current,
    options = [],
}) => {
    /**
     * Adds specified locale segment to a URI
     * @param {string} string
     * @returns {string}
     */
    const formatURI = (uri, locale) => {
        try {
            const location = getLocation();

            const url = new URL(uri, location.origin);

            // Ignore external links
            if (url.origin !== location.origin) { return url.href; }

            let segments = url.pathname.split('/').filter(s => s.length > 0);

            // Remove any existing language segment if found
            if (segments.length > 0 && options.find((o) => o.segment === segments[0])) {
                segments.shift();
            }

            // Select required language segment
            const localeSegment = locale !== undefined ? options.find((o) => o.code === locale)?.segment || null : current?.segment;

            // Compose new path
            url.pathname = `${localeSegment ? `/${localeSegment}` : ''}/${segments.join('/')}`;

            return url.href.replace(/\/$/, '');
        } catch (err) {
            console.error(err);
            return uri;
        }
    };

    /**
     * Translate a string
     * @param {string} string
     * @returns {string}
     */
    const translate = (string) => {
        const translations = Translations[current?.code];
        if (!translations) { return string; }

        return translations[string] || translations[string?.toLowerCase] || string;
    };

    return (
        <LocaleContext.Provider value={useMemo(() => {
            return {
                options,
                current,
                formatURI,
                translate
            };
        }, [
            options,
            current,
            formatURI,
            translate
        ])}>
            {children}
        </LocaleContext.Provider>
    );
};

LocaleProvider.propTypes = {
    children: PropTypes.any,
    current: PropTypes.object,
    options: PropTypes.array,
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export {
    LocaleProvider,
    useLocale
};
